(function () {
  // Test reviews: keep excluded even if stale Judge.me widget data includes them.
  const EXCLUDED_REVIEW_IDS = new Set([
    'd9cf50ff-e468-440d-9ee1-b11555e526e3',
    'e27db8df-4611-4bed-90a1-0be4d5f5fec3',
  ]);

  function isExcludedReview(node) {
    return ['data-id', 'data-review-id', 'data-review-uuid'].some((attribute) =>
      EXCLUDED_REVIEW_IDS.has(node.getAttribute(attribute))
    );
  }

  const REVIEW_SELECTORS = [
    '.jdgm-rev',
    '.jdgm-rev-widg__review',
    '[data-review-id]',
    '.jdgm-rev__content',
    '.jdgm-all-reviews-widget .jdgm-rev',
    '.jdgm-all-reviews-widget-v2025 [class*="review"]',
  ];

  function normalizeText(value) {
    return (value || '').toLowerCase().trim();
  }

  function getReviewNodes(widget) {
    const nodes = new Set();
    const seenReviewIds = new Set();

    REVIEW_SELECTORS.forEach((selector) => {
      widget.querySelectorAll(selector).forEach((node) => {
        const item =
          node.closest('.jdgm-rev') ||
          node.closest('[data-review-id]') ||
          (node.matches('.jdgm-rev') ? node : null);

        if (item && widget.contains(item)) {
          const reviewId = item.matches('.jdgm-rev') ? item.getAttribute('data-review-id') : null;

          if (isExcludedReview(item)) {
            item.dataset.dhuniReviewHidden = 'true';
          } else if (reviewId && seenReviewIds.has(reviewId) && !nodes.has(item)) {
            // Page 1 and Judge.me's page 2+ are cached separately, so a review
            // can appear on both while a new review shifts the pages.
            item.remove();
          } else {
            if (reviewId) seenReviewIds.add(reviewId);
            nodes.add(item);
          }
        }
      });
    });

    return Array.from(nodes);
  }

  const LIVE_REVIEWS_PATH = '/reviews/all_reviews_js_based';
  const JUDGEME_READY_TIMEOUT = 15000;

  function waitForJudgeme() {
    const startedAt = Date.now();

    return new Promise((resolve) => {
      (function check() {
        const jdgm = window.jdgm;
        if (jdgm && jdgm.$ && jdgm.ajaxParamsFor && jdgm.customizeReviews && jdgm.triggerEvent && jdgm.SPECIAL_CDN_HOST) {
          resolve(jdgm);
          return;
        }
        if (Date.now() - startedAt > JUDGEME_READY_TIMEOUT) {
          resolve(null);
          return;
        }
        window.setTimeout(check, 200);
      })();
    });
  }

  function getReviewText(node) {
    return normalizeText(node.textContent);
  }

  const PRODUCT_IMAGE_CACHE = new Map();

  function extractProductHandle(href) {
    if (!href) return null;
    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (_) {
      return null;
    }
  }

  function findProductLink(node) {
    return node.querySelector('a[href*="/products/"]');
  }

  function fetchProductFirstImage(handle) {
    if (PRODUCT_IMAGE_CACHE.has(handle)) return PRODUCT_IMAGE_CACHE.get(handle);

    const promise = fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        const image = product && Array.isArray(product.images) && product.images.length
          ? product.images[0]
          : null;
        return image ? { url: image, title: product.title || '' } : null;
      })
      .catch(() => null);

    PRODUCT_IMAGE_CACHE.set(handle, promise);
    return promise;
  }

  function attachProductThumb(node, link) {
    const handle = extractProductHandle(link.getAttribute('href'));
    if (!handle) return;
    node.dataset.dhuniReviewThumbDone = 'true';

    fetchProductFirstImage(handle).then((result) => {
      if (!result || node.querySelector('.dhuni-reviews-page__thumb')) return;

      const wrapper = document.createElement('a');
      wrapper.className = 'dhuni-reviews-page__thumb';
      wrapper.href = link.getAttribute('href');
      wrapper.setAttribute('aria-label', result.title ? `${result.title}の商品ページを見る` : '商品ページを見る');

      const img = document.createElement('img');
      img.src = result.url;
      img.alt = '';
      img.loading = 'lazy';

      wrapper.appendChild(img);
      node.prepend(wrapper);
    });
  }

  function updateCount(countEl, visible, total, loading) {
    if (!countEl) return;

    if (loading) {
      countEl.textContent = 'レビューを読み込み中…';
      return;
    }

    if (total === 0) {
      countEl.textContent = 'レビューが見つかりません';
      return;
    }

    countEl.textContent = `${visible}件表示（全${total}件）`;
  }

  class DhuniReviewsPage {
    constructor(root) {
      this.root = root;
      this.widget = root.querySelector('[data-dhuni-reviews-widget]');
      this.searchInput = root.querySelector('[data-dhuni-reviews-search]');
      this.filterButtons = Array.from(root.querySelectorAll('[data-dhuni-reviews-filter]'));
      this.countEl = root.querySelector('[data-dhuni-reviews-count]');
      this.activeKeyword = '';
      this.reviewNodes = [];
      this.observer = null;
      this.loadAttempts = 0;

      this.onSearch = this.onSearch.bind(this);
      this.onFilterClick = this.onFilterClick.bind(this);
      this.refresh = this.refresh.bind(this);

      this.bindEvents();
      this.startObserver();
      this.refresh();
      this.scheduleRefresh();
      this.loadLiveFirstPage();
    }

    // Page 1 is rendered from Judge.me's metafield snapshot, which can stop
    // syncing, while Judge.me's own script loads page 2+ from its live API.
    // Replacing page 1 from the same API keeps both on the same data; if the
    // request fails the snapshot stays as it is.
    loadLiveFirstPage() {
      const list = this.widget && this.widget.querySelector('[data-dhuni-reviews-list]');
      const allReviewsWidget = list && list.closest('.jdgm-all-reviews-widget');
      if (!allReviewsWidget) return;

      list.querySelectorAll('.jdgm-rev').forEach((node) => {
        node.dataset.dhuniReviewSnapshot = 'true';
      });

      waitForJudgeme()
        .then((jdgm) => {
          if (!jdgm) return null;

          // Same host and params as Judge.me's page 2+ requests, so both hit the same cache.
          const params = new URLSearchParams(jdgm.ajaxParamsFor(jdgm.$(allReviewsWidget)));
          params.set('review_type', 'product-reviews');

          return fetch(`https://${jdgm.SPECIAL_CDN_HOST}${LIVE_REVIEWS_PATH}?${params}`)
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
              if (data && data.html) this.replaceSnapshot(jdgm, list, data.html);
            });
        })
        .catch(() => {});
    }

    replaceSnapshot(jdgm, list, html) {
      const $list = jdgm.$(list);

      // Reviews Judge.me already appended from page 2+ stay after the new page 1.
      $list.find('[data-dhuni-review-snapshot]').remove();
      $list.prepend(html);

      // Same setup Judge.me runs after appending reviews (stars, dates, photos).
      const $reviews = $list.find(`.jdgm-rev:not(.${jdgm.DONE_SETUP_CLASS})`);
      jdgm.triggerEvent('doneAppendMoreReviewsInAllReviewsPage', { $reviews });
      jdgm.customizeReviews();
      if (jdgm.setupMediaGallery) jdgm.setupMediaGallery($list);
    }

    bindEvents() {
      if (this.searchInput) {
        this.searchInput.addEventListener('input', this.onSearch);
      }

      this.filterButtons.forEach((button) => {
        button.addEventListener('click', this.onFilterClick);
      });
    }

    scheduleRefresh() {
      if (this.loadAttempts >= 20) return;

      this.loadAttempts += 1;
      window.setTimeout(() => {
        this.refresh();
        if (this.reviewNodes.length === 0) this.scheduleRefresh();
      }, 500);
    }

    startObserver() {
      if (!this.widget || typeof MutationObserver === 'undefined') return;

      this.observer = new MutationObserver(() => {
        window.requestAnimationFrame(this.refresh);
      });

      this.observer.observe(this.widget, {
        childList: true,
        subtree: true,
      });
    }

    onSearch() {
      this.applyFilters();
    }

    onFilterClick(event) {
      const button = event.currentTarget;
      const keyword = button.dataset.filterKeyword || '';

      this.filterButtons.forEach((item) => {
        const isActive = item === button;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.activeKeyword = keyword;
      this.applyFilters();
    }

    applyFilters() {
      const query = normalizeText(this.searchInput?.value);
      const keyword = normalizeText(this.activeKeyword);
      const hasFilter = Boolean(query || keyword);
      let visible = 0;

      if (this.reviewNodes.length === 0) {
        updateCount(this.countEl, 0, 0, true);
        return;
      }

      this.reviewNodes.forEach((node) => {
        if (!hasFilter) {
          node.dataset.dhuniReviewHidden = 'false';
          visible += 1;
          return;
        }

        const text = getReviewText(node);
        const matchesSearch = !query || text.includes(query);
        const matchesKeyword = !keyword || text.includes(keyword);
        const show = matchesSearch && matchesKeyword;

        node.dataset.dhuniReviewHidden = show ? 'false' : 'true';
        if (show) visible += 1;
      });

      updateCount(this.countEl, visible, this.reviewNodes.length, false);
    }

    refresh() {
      if (!this.widget) return;
      this.reviewNodes = getReviewNodes(this.widget);
      this.attachProductThumbs();
      this.applyFilters();
    }

    attachProductThumbs() {
      this.reviewNodes.forEach((node) => {
        if (node.dataset.dhuniReviewThumbDone === 'true') return;
        const link = findProductLink(node);
        if (link) attachProductThumb(node, link);
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-dhuni-reviews-page]').forEach((root) => {
      if (root.dataset.dhuniReviewsInit === 'true') return;
      root.dataset.dhuniReviewsInit = 'true';
      new DhuniReviewsPage(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
